import CopyButton from '@/components/CopyButton'
import api from '@/utils/api'
import { ActionIcon, Box, Button, Checkbox, Divider, List, Loader, Modal, Paper, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { IconInfoCircle, IconInfoSquare, IconRefresh } from '@tabler/icons-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { APIAccess, APICapability } from '../../../types/global'
import { API_CAPABILITIES } from '@/utils/data/apiCapabilities'
import Error from '@/components/Error'

const routes = [
    {
        href: "",
        title: "Getting Started"
    },
    {
        href: "/authentication",
        title: "Authentication"
    },
    {
        href: "/managing-links",
        title: "Managing Links"
    },
    {
        href: "/managing-clients",
        title: "Managing Clients"
    }
] as { href: string, title: string }[]

const Navigation = () => {
    const router = useRouter()
    return (
        <Box className='flex fdc' miw={175} style={{ position: "sticky", top: 0 }}>
            <Text fz="xs" fw={800} c="dimmed">SECTIONS</Text>
            <Box mt={5}>
                {routes.map((route, i) => {
                    const completePath = `/dashboard/api${route.href}`
                    const selected = router.pathname === completePath
                    return (
                        <>
                            {!!i ? <Divider opacity={.5} /> : null}
                            <Paper component={Link} href={completePath} p="xs" shadow={selected ? "xs" : "none"} withBorder={selected} bg={!selected ? "transparent" : undefined}>
                                <Text fz="sm">{route.title}</Text>
                            </Paper>
                        </>
                    )
                })}

            </Box>
        </Box>
    )
}

const CreateKeyModal = ({ close, setAccess }: { close: VoidFunction, setAccess: (access: APIAccess) => void }) => {
    const [capabilities, setCapabilities] = useState<APICapability[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    return (
        <Box>
            <Box>
                <Text fw={500} fz="sm">Select Capabilities</Text>
                <Text fz="xs" c="dimmed">Select the capabilities you'd like to enable for this key</Text>
                <Box className='flex fdc' style={{ gap: 5 }} mt={8}>
                    {API_CAPABILITIES.map((c) => <Checkbox
                        checked={capabilities.includes(c)}
                        onChange={(e) => setCapabilities((prev) => e.target.checked ? [...prev, c] : [...prev.filter(ca => ca !== c)])} label={c}
                    />
                    )}
                </Box>
                <Button mt={10} variant='subtle' color='blue' size='compact-sm' onClick={() => setCapabilities(API_CAPABILITIES.length === capabilities.length ? [] : API_CAPABILITIES)}>{`${capabilities.length === API_CAPABILITIES.length ? "Deselect" : "Select"} all`}</Button>
            </Box>
            <Button loading={loading} disabled={!capabilities.length} fullWidth mt={15} onClick={() => {
                setLoading(true)
                api.post<APIAccess>("/agency/api/key/create", {
                    capabilities
                }).then((res) => {
                    setLoading(false)
                    if (res.error) return setError(res.msg)
                    setAccess(res.data)
                    close()
                })
            }}>Create Key</Button>
            <Error mt={10}>{error}</Error>
        </Box>
    )
}

const KeyManagement = () => {
    const [access, setAccess] = useState<undefined | null | APIAccess>(undefined)
    const [error, setError] = useState("")
    const [createModal, setCreateModal] = useState(false)

    useEffect(() => {
        api.get<APIAccess | null>("/agency/api/key").then((res) => {
            if (res.error) return setError(res.msg);
            setAccess(res.data)
        })
    }, [])

    return (
        <>
            <Modal
                opened={createModal}
                onClose={() => setCreateModal(false)}
                title={<Text fw={500}>Create a new key</Text>}
                styles={{ header: { minHeight: 0 } }}
            >
                <CreateKeyModal
                    close={() => setCreateModal(false)}
                    setAccess={setAccess}
                />
            </Modal>

            <Paper p={0} shadow='xs' w={225} style={{ position: "sticky", top: 0 }}>
                <Box p="sm" className='flex aic jcsb' style={{ gap: 10 }}>
                    <Text fz="sm" fw={500}>API Key</Text>
                    {!!access
                        ? <ActionIcon onClick={() => setCreateModal(true)} variant='subtle' size={"sm"} radius={5}>
                            <IconRefresh size={15} />
                        </ActionIcon>
                        : null
                    }
                </Box>
                <Divider opacity={.5} />
                <Box p="sm">
                    <Error>{error}</Error>
                    {access === null
                        ? <Box>
                            <Button onClick={() => setCreateModal(true)} fullWidth>Generate Key</Button>
                        </Box>
                        : <Box>
                            <Box className='flex aic jcsb'>
                                {access === undefined ? <Loader size={14} /> : <Text className='data-font' fz="xs">{access.key.substring(0, 10)}••••</Text>}
                                <Box className='flex aic' style={{ gap: 0 }}>
                                    <Tooltip multiline label={<>
                                        <Text fw={600} fz="xs">Capabilities</Text>
                                        <List fz="xs">
                                            {access?.capabilities.map((c) => <List.Item>{c}</List.Item>)}
                                        </List>
                                    </>}>
                                        <ActionIcon variant='subtle'>
                                            <IconInfoCircle size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <CopyButton value={access?.key || ""} />
                                </Box>
                            </Box>
                            <Box mt={5} className='flex' style={{ gap: 5 }}>
                                <IconInfoSquare style={{ marginTop: 3, minWidth: 13 }} size={13} color='var(--mantine-color-dimmed)' />
                                <Text fz="xs" c="dimmed">Store this key securely inside an envrionment variable. Never expose this key to the public.</Text>
                            </Box>
                        </Box>
                    }

                </Box>

            </Paper>
        </>
    )
}

const ArticleNavButtons = () => {
    const router = useRouter()
    const selectedRouteIndex = routes.findIndex((r) => `/dashboard/api${r.href}` === router.pathname)
    const prevRoute = routes[selectedRouteIndex - 1] || null
    const nextRoute = routes[selectedRouteIndex + 1] || null

    const NavBtn = ({ title, href, isNext }: { href: string, title: string, isNext: boolean }) => <UnstyledButton style={{ flex: 1, maxWidth: "50%" }} component={Link} href={`/dashboard/api${href}`}>
        <Paper w={"100%"} withBorder shadow='xs' p="sm" pt={25} style={{ flex: 1 }}>
            <Text lh={1} c="dimmed" fz="sm">{isNext ? "Next" : "Prev"}</Text>
            <Text fw={500}>{title}</Text>
        </Paper>
    </UnstyledButton>

    return (
        <Box mt={20} className='flex aic' style={{ gap: 10 }}>
            {prevRoute
                ? <NavBtn isNext={false} href={prevRoute.href} title={prevRoute.title} />
                : null
            }
            {nextRoute
                ? <NavBtn isNext href={nextRoute.href} title={nextRoute.title} />
                : null
            }
        </Box>
    )
}

export default function APILayout({ children }: PropsWithChildren) {
    return (
        <Box pb={40} className='flex' style={{ gap: 30 }}>
            <Box>
                <Navigation />
            </Box>
            <Box style={{ flex: 1 }} py={30}>
                {children}
                <ArticleNavButtons />
            </Box>
            <Box>
                <KeyManagement />
            </Box>
        </Box>
    )
}
